var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { KlevuConfig } from "../index.js";
import { injectFilterResult } from "../modifiers/injectFilterResult/injectFilterResult.js";
import { KlevuFetchCache } from "../store/klevuFetchCache.js";
import { post } from "./fetch.js";
import { getAnnotationsForProduct } from "./resultHelpers/getAnnotationsForProduct.js";
import { fetchNextPage } from "./resultHelpers/fetchNextPage.js";
import { FetchResultEvents } from "./resultHelpers/FetchResultEvents.js";
export const klevuFetchCache = new KlevuFetchCache();
/**
 * Function that makes query to KlevuBackend. It can take amount of queries.
 *
 * @category KlevuFetch
 * @param functions list of functions to execute
 * @returns Tools to operate results and get next results {@link KlevuFetchResponse}
 */
export function KlevuFetch(...functionPromises) {
    var _a, _b, _c, _d, _e;
    return __awaiter(this, void 0, void 0, function* () {
        if (functionPromises.length < 1) {
            throw new Error("At least one fetch function should be provided to fetch.");
        }
        const functions = yield Promise.all(functionPromises);
        const { recordQueries, suggestionQueries } = yield cleanAndProcessFunctions(functions);
        const withOverride = functions.find((f) => Boolean(f.configOverride));
        const payload = {
            context: {
                apiKeys: [
                    (_b = (_a = withOverride === null || withOverride === void 0 ? void 0 : withOverride.configOverride) === null || _a === void 0 ? void 0 : _a.apiKey) !== null && _b !== void 0 ? _b : KlevuConfig.getDefault().apiKey,
                ],
            },
            recordQueries: recordQueries.length > 0 ? recordQueries : undefined,
            suggestions: suggestionQueries.length > 0 ? suggestionQueries : undefined,
        };
        const cached = klevuFetchCache.check(payload);
        let response;
        if (cached) {
            response = cached;
        }
        else {
            const res = yield post((_d = (_c = withOverride === null || withOverride === void 0 ? void 0 : withOverride.configOverride) === null || _c === void 0 ? void 0 : _c.url) !== null && _d !== void 0 ? _d : KlevuConfig.getDefault().url, payload);
            if (!res) {
                throw new Error("Couldn't fetch data");
            }
            response = res;
            if (((_e = res.meta) === null || _e === void 0 ? void 0 : _e.responseCode) == 200) {
                klevuFetchCache.cache(payload, response);
            }
        }
        return KlevuCreateResponseObject(response, functions);
    });
}
export function KlevuCreateResponseObject(response, queries) {
    let responseObject = {
        apiResponse: response,
        suggestionsById: (id) => { var _a; return (_a = response.suggestionResults) === null || _a === void 0 ? void 0 : _a.find((q) => q.id === id); },
        queriesById: (id) => KlevuQueriesById(id, response, queries),
    };
    // Send event to functions on result
    for (const f of queries) {
        if (f.modifiers) {
            for (const modifier of f.modifiers) {
                if (modifier.onResult) {
                    responseObject = modifier.onResult(responseObject, f);
                }
            }
        }
    }
    return responseObject;
}
/**
 *
 * @param id Id if response to find
 * @param response Raw API response from server
 * @param queries Queries used to create API response
 * @returns
 */
function KlevuQueriesById(id, response, queries) {
    var _a;
    const res = (_a = response.queryResults) === null || _a === void 0 ? void 0 : _a.find((s) => s.id === id);
    if (!res) {
        return undefined;
    }
    const func = queries.find((f) => { var _a; return (_a = f.queries) === null || _a === void 0 ? void 0 : _a.some((q) => q.id == res.id); });
    if (!func) {
        return Object.assign(Object.assign({}, res), { annotationsById: (productId, languageCode) => getAnnotationsForProduct(res, productId, languageCode) });
    }
    const result = Object.assign(Object.assign(Object.assign({}, res), FetchResultEvents(res, func)), { functionParams: func.params, annotationsById: (productId, languageCode) => getAnnotationsForProduct(res, productId, languageCode), next: fetchNextPage({ response, func }), getPage: fetchNextPage({
            response,
            func,
            ignoreLastPageUndefined: true,
        }) });
    return result;
}
function cleanAndProcessFunctions(functions) {
    return __awaiter(this, void 0, void 0, function* () {
        const recordQueries = [];
        const suggestionQueries = [];
        for (const f of functions) {
            if (f.queries) {
                let qs = [...f.queries];
                if (f.modifiers) {
                    for (const modifier of f.modifiers) {
                        if (modifier.modifyAfter) {
                            qs = yield modifier.modifyAfter(qs, f);
                        }
                    }
                }
                recordQueries.push(...qs);
            }
            if (f.suggestions) {
                suggestionQueries.push(...f.suggestions);
            }
        }
        // Check for duplicate id's
        for (const query of recordQueries) {
            if (recordQueries.filter((q) => q.id === query.id).length !== 1) {
                throw new Error("Duplicate ids in request. Please provider unique ids for requests");
            }
        }
        return {
            recordQueries,
            suggestionQueries,
        };
    });
}
/**
 * Removes list filters from query
 *
 * @param f
 * @param prevQueryResult
 * @returns
 */
export function removeListFilters(f, prevQueryResult) {
    var _a;
    f.queries = (_a = f.queries) === null || _a === void 0 ? void 0 : _a.map((q) => {
        var _a, _b;
        if ((_a = q.filters) === null || _a === void 0 ? void 0 : _a.filtersToReturn) {
            (_b = q.filters) === null || _b === void 0 ? true : delete _b.filtersToReturn;
        }
        return q;
    });
    if (f.modifiers) {
        const index = f.modifiers.findIndex((m) => m.klevuModifierId == "listfilters");
        if (index > -1) {
            f.modifiers.splice(index, 1);
        }
        f.modifiers.push(injectFilterResult(prevQueryResult));
    }
    return f;
}
